import React, { useEffect } from 'react';
import { Spin, Alert, Typography } from 'antd';
import { Banner } from 'components/ui/Banner';
import PageMeta from 'components/PageMeta/PageMeta';
import Card from 'components/Card/Card';
import { getPreviousStep, getNextStep, stepConfigAtom } from '../../atoms';
import { FAMILY_APPLICATION_STEPS } from '../../familyApplicationConstants';
import { useAtomValue } from 'jotai';
import familyService from 'services/family.service';
import { useAsyncCallback } from 'react-async-hook';
import classNames from 'classnames';
import { RevealingLink } from 'components/ui/RevealingLink/RevealingLink';
import { ApplicationWizardActions } from 'components/application-wizards/ApplicationWizardActions';

export function FamilyAppEligibilitySummary({ application, loading, setStep, readOnly = false }) {
  const stepConfig = useAtomValue(stepConfigAtom);

  const isCompletedBySponsor = application?.isCompletedBySponsor;

  // Fetch eligibility data
  const {
    loading: loadingEligibility,
    result: eligibilityData,
    execute: fetchEligibility,
  } = useAsyncCallback(async () => {
    if (!application?.id) return null;

    try {
      const data = await familyService.getApplicationEligibility(application.id);
      return data;
    } catch (error) {
      console.error('Error fetching eligibility data:', error);
      return null;
    }
  });

  // Fetch eligibility data when application ID changes
  useEffect(() => {
    if (application?.id) {
      fetchEligibility();
    }
  }, [fetchEligibility, application?.id]);

  const getEligibilityMessage = (eligibility) => {
    switch (eligibility) {
      case 'FullTime':
        return 'May qualify for full-time fee assistance';
      case 'PartTime':
        return 'May be eligible for part-time fee assistance';
      case 'NotEligible':
        return 'Not currently eligible based on reported hours';
      default:
        return 'I other';
    }
  };

  const getEligibilityColor = (eligibility) => {
    switch (eligibility) {
      case 'FullTime':
      case 'PartTime':
        return 'success';
      case 'NotEligible':
        return 'warning';
      default:
        return 'info';
    }
  };

  // Data for eligibility rows
  const eligibilityRows = [
    ...[
      {
        label: 'Salaried (or hourly) Employment hours:',
        dataKey: 'employmentHoursTotal',
        isTotal: false,
      },
      {
        label: 'Self-employment hours:',
        dataKey: 'selfEmploymentHoursTotal',
        isTotal: false,
      },
      {
        label: 'Military service hours:',
        dataKey: 'militaryHours',
        isTotal: false,
      },
      {
        label: 'Education hours:',
        dataKey: 'educationHoursTotal',
        isTotal: false,
      },
    ].filter((row) => eligibilityData?.[row?.dataKey] > 0),
    ...(eligibilityData?.eligibility === 'NotEligible'
      ? [
          {
            label: 'Other qualifying circumstances',
            dataKey: 'requiresReview',
            isReview: true,
          },
        ]
      : []),
    ...(eligibilityData?.totalWeeklyHours > 0 && eligibilityData?.eligibility !== 'NotEligible'
      ? [
          {
            label: 'Total weekly hours:',
            dataKey: 'totalWeeklyHours',
            isTotal: true,
          },
        ]
      : []),
  ];

  if (loading || loadingEligibility) {
    return (
      <div className="p-8">
        <Spin className="table m-auto" />
      </div>
    );
  }

  return (
    <div>
      <PageMeta title="Eligibility Summary" />

      <div className="p-7 pt-0">
        <Banner question={`${isCompletedBySponsor ? 'Their' : 'Your'} Eligibility Summary`} className="my-12" />

        {eligibilityData && (
          <Card color="primary" title="Your Eligibility Calculation">
            <div className="space-y-3">
              {eligibilityRows.map((row, index) => (
                <EligibilityRow
                  key={index}
                  label={row.label}
                  value={eligibilityData[row.dataKey]}
                  isTotal={row.isTotal}
                  isReview={row.isReview}
                  extra={
                    row.dataKey === 'educationHoursTotal' ? (
                      <RevealingLink
                        text="How we calculated your education hours"
                        reveals={
                          <Alert
                            type="info"
                            showIcon
                            description={`We count your total study commitment including class time and homework. Your ${
                              eligibilityData?.totalCreditHours
                            } credit${eligibilityData?.totalCreditHours === 1 ? '' : 's'} equal ${
                              eligibilityData?.educationHoursTotal
                            } hour${
                              eligibilityData?.educationHoursTotal === 1 ? '' : 's'
                            } per week based on the type of courses you're taking.`}
                          />
                        }
                        className="mt-1"
                      />
                    ) : null
                  }
                />
              ))}
            </div>

            <div className="mt-6">
              <Alert
                type={getEligibilityColor(eligibilityData.eligibility)}
                message={getEligibilityMessage(eligibilityData.eligibility)}
                description={
                  eligibilityData.eligibility === 'NotEligible' && (
                    <>
                      Your current weekly work and/or <strong className="font-bold">education hours</strong> are below
                      the minimum needed for fee assistance (16-24 hours for part-time care or 25+ hours for full-time
                      care)
                    </>
                  )
                }
                showIcon
              />

              {eligibilityData.eligibility === 'NotEligible' && (
                <Alert
                  message={
                    <>
                      {' '}
                      In some cases, families may receive up to 180 days of full- or part-time fee assistance while a
                      spouse/domestic partner looks for work or increases their hours, with a required check-in at 90
                      days.
                    </>
                  }
                  description="We’ll ask about this in the next step"
                  showIcon
                  className="mt-4"
                />
              )}
            </div>

            {eligibilityData.eligibility !== 'NotEligible' && (
              <Typography.Text type="secondary" className="block mt-4 text-sm">
                <p>
                  <strong className="font-semibold">Note:</strong> Final eligibility depends on income and other factors
                </p>
              </Typography.Text>
            )}
          </Card>
        )}

        {!eligibilityData && (
          <Alert
            type="warning"
            message="Unable to load eligibility data"
            description="Please try refreshing the page, or contact support if the problem persists"
            showIcon
          />
        )}
      </div>

      <ApplicationWizardActions
        applicationIsReadOnly={readOnly}
        backButtonProps={{
          onClick: () => {
            // Use getPreviousStep to determine the previous step
            const previousStep = getPreviousStep(FAMILY_APPLICATION_STEPS.ELIGIBILITY_SUMMARY, application, stepConfig);

            setStep({
              applicationId: application?.id,
              newStep: previousStep,
            });
          },
        }}
        nextButtonProps={{
          disabled: false,
          onClick: () => {
            // Move to next step
            const nextStep = getNextStep(FAMILY_APPLICATION_STEPS.ELIGIBILITY_SUMMARY, application, stepConfig);
            setStep({
              applicationId: application.id,
              newStep: nextStep,
            });
          },
          text: 'Next',
        }}
      />
    </div>
  );
}

// Reusable component for eligibility rows
const EligibilityRow = ({ label, value, isTotal = false, isReview = false, extra = null }) => {
  return (
    <div
      className={classNames('text-black border-b border-solid border-blue-200 pb-3', {
        'pb-12': isTotal,
      })}
    >
      <div className="flex justify-between items-center">
        <span
          className={classNames({
            'font-bold text-lg': isTotal,
          })}
        >
          {label}
        </span>

        <div className="flex items-center gap-2">
          <span
            className={classNames({
              'font-bold text-lg': isTotal,
            })}
          >
            {isReview ? 'Pending review' : `${(value || 0)?.toLocaleString?.()} hour${value === 1 ? '' : 's'}`}
          </span>
        </div>
      </div>

      {extra}
    </div>
  );
};

export default FamilyAppEligibilitySummary;
